import rootRender from '@react/utils/root-render';
import SearchAndFilter from './index';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { RefObject, createRef, useEffect, useRef, useState } from 'react';
import { PageData } from './types';
import { getDataFromPage } from '../../utils';
import clsx from 'clsx';
import he from 'he';

const queryClient = new QueryClient();

type TabNames = 'events' | 'webinars';

function Tabs() {
  const pageData = useRef<PageData>(getDataFromPage('react-search-data'));
  const shouldFetch = useRef(true);

  const {
    hasUpcomingEvents = true,
    strings: { eventsTab, eventsText, webinarsTab, webinarsText, noEventsTitle, noEventsText },
  } = pageData.current;
  const [activeTab, setActiveTab] = useState<TabNames>(
    hasUpcomingEvents ? 'events' : 'webinars'
  );
  const [highlightOffset, setHighlightOffset] = useState<number>(0);
  const [highlightWidth, setHighlightWidth] = useState<number>(0);
  

  const tabs: { name: TabNames; text: string }[] = [
    { name: 'events', text: eventsTab },
    { name: 'webinars', text: webinarsTab },
  ];

  const initialRefs:
    | Record<string, never>
    | { [key in TabNames]: RefObject<HTMLButtonElement> } = tabs.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.name]: createRef(),
    }),
    {}
  );

  const tabEls = useRef(initialRefs);

  const handleClick = (name: TabNames) => {
    setActiveTab(name);
    if (!shouldFetch.current) shouldFetch.current = true;
  };

  useEffect(() => {
    const activeEl = tabEls.current[activeTab] as RefObject<HTMLButtonElement>;

    setHighlightWidth(activeEl.current.offsetWidth);
    setHighlightOffset(activeEl.current.offsetLeft);
  }, [activeTab]);

  return (
    <div
      className="tab-search"
      style={
        {
          '--highlight-offset': `${highlightOffset}px`,
          '--highlight-width': `${highlightWidth}px`,
        } as React.CSSProperties
      }
      >
      <hr className="tab-search__rule rule rule--full-width" />
      <div className="tab-search__tabs">
        {tabs.map(tab => {
          const selected = activeTab === tab.name;

          return (
            <button
              key={tab.name}
              type="button"
              className={clsx('tab-search__tab', {
                selected,
              })}
              onClick={() => handleClick(tab.name)}
              ref={tabEls.current[tab.name]}
              tabIndex={selected ? -1 : 0}
            >
              {he.decode(tab.text)}
            </button>
          );
        })}
        <hr className="tab-search__tab-highlight" />
      </div>
      <hr className="tab-search__rule rule rule--full-width" />
      <p className="tab-search__description">
        {activeTab === 'events'
          ? he.decode(eventsText)
          : he.decode(webinarsText)}
        {!hasUpcomingEvents && activeTab !== 'webinars' ? (
        <div className="noevents-info">
          {noEventsTitle && noEventsTitle.length > 0 ? (<p className="noevents-info__title">{noEventsTitle}</p>) : null }
          {noEventsText && noEventsText.length > 0 ? (<p className="noevents-info__text">{noEventsText}</p>) : null }
        </div>) : null }
      </p>
      <SearchAndFilter
        isWebinar={activeTab === 'webinars'}
        shouldFetch={shouldFetch.current}
      />
    </div>
  );
}

rootRender(
  'react-event-search',
  <QueryClientProvider client={queryClient}>
    <Tabs />
  </QueryClientProvider>
);
